import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Control, useWatch } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InvoicesKeys,
  patchInvoiceHeader,
} from "@/features/invoices/api/invoices";
import { currencies } from "@/utils/currencies";

import { InvoiceFormValues } from "./types";

interface CurrencySelectorProps {
  control: Control<InvoiceFormValues>;
  disabled?: boolean;
  invoiceId?: string | null;
  onCurrencyChange?: () => void;
}

const CurrencySelector = ({
  control,
  disabled,
  invoiceId,
  onCurrencyChange,
}: CurrencySelectorProps) => {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const dueDate = useWatch({
    control,
    name: "dueDate",
  });

  const { mutate: updateCurrency } = useMutation({
    mutationFn: (currency: string) => {
      // Include the current due date in the API call
      const dueDateTimestamp = dueDate
        ? Math.floor(new Date(dueDate).getTime() / 1000)
        : undefined;

      return patchInvoiceHeader(invoiceId!, {
        currency,
        dueDate: dueDateTimestamp,
      });
    },
    onSuccess: () => {
      // Invalidate invoices query after updating currency
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
    },
  });

  // Watch for currency changes
  const currency = useWatch({
    control,
    name: "currency",
    defaultValue: "USD",
  });

  // Handle currency change
  const handleCurrencyChange = (value: string) => {
    if (!invoiceId) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      updateCurrency(value);
    }, 300);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <FormField
      control={control}
      name="currency"
      render={({ field }) => (
        <FormItem className="space-y-2 max-w-[200px]">
          <FormLabel>Currency</FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              handleCurrencyChange(value);
              if (onCurrencyChange) {
                onCurrencyChange();
              }
            }}
            value={field.value}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {currencies.map((currency) => (
                <SelectItem
                  key={currency.code}
                  value={currency.code}
                  className="flex items-center gap-2"
                >
                  <span className="font-medium">{currency.code}</span>
                  <span className="text-muted-foreground">
                    ({currency.symbol}) {currency.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default CurrencySelector;
